import React from "react";
import PropTypes from "prop-types";
import { TemplateSelector } from "./templates/TemplateSelector/TemplateSelector";
import { Button } from "../../shared/Button/Button";
import { Modal } from "../../shared/Modal/Modal";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { MicMuteIcon } from "../../assets/Icons/MicMuteIcon";
import { MicIcon } from "../../assets/Icons/MicIcon";
import { ThunderIcon } from "../../assets/Icons/ThunderIcon";
import { PageOverlayLoader } from "../../shared/PageOverlayLoader/PageOverlayLoader";
import { useDictation } from "./hooks/useDictation";
import classes from "./Dictation.module.scss";

export function Dictation({
  value,
  onChange,
  transcription,
  setProcedureNotes,
  hideNoteSaveOption = false,
  onSaveNote,
}) {
  const { tCommon } = useAppTranslation.Common();
  const {
    isRecording,
    isTranscribing,
    isChartSmartOpen,
    isGeneratingNote,
    isLimitReached,
    setIsLimitReached,
    onStartNewRecording,
    onToggle,
    onChartSmartClick,
    onTemplateSubmit,
    setIsChartSmartOpen,
    subscriptionError,
    resetSubscriptionError,
  } = useDictation({
    text: value,
    onReadText: onChange,
    setProcedureNotes,
    transcription,
    onSaveNote,
  });

  return (
    <div className={classes.container}>
      <div className={classes.mainContent}>
        <div className={classes.buttonGroup}>
          <Button
            variant="outlined"
            color={isRecording ? "error" : undefined}
            leftAdornment={isRecording ? <MicMuteIcon /> : <MicIcon />}
            onClick={onToggle}
            disabled={isTranscribing || isChartSmartOpen}
            className={classes.recordButton}
          >
            {isRecording
              ? tCommon("dictation.button.stop")
              : tCommon("dictation.button.start")}
          </Button>

          <Button
            variant="outlined"
            leftAdornment={<ThunderIcon />}
            className={classes.chartSmartButton}
            onClick={onChartSmartClick}
            disabled={
              (!value && !transcription) || isTranscribing || isRecording
            }
          >
            {tCommon("dictation.button.chartSmart")}
          </Button>
        </div>

        {isChartSmartOpen && (
          <TemplateSelector
            text={value}
            onChange={onTemplateSubmit}
            isSubmitting={isGeneratingNote}
            onClose={() => setIsChartSmartOpen(false)}
            hideNoteSaveOption={hideNoteSaveOption}
            subscriptionError={subscriptionError}
            resetSubscriptionError={resetSubscriptionError}
          />
        )}

        {isLimitReached && (
          <Modal
            isOpen
            footerNoBorder
            withCloseBtn={false}
            shouldCloseOnOverlayClick={false}
            header={
              <Modal.Title>
                {tCommon("dictation.message.limitReached")}
              </Modal.Title>
            }
            footer={
              <div className={classes.limitFooter}>
                <Button onClick={onStartNewRecording} disabled={isTranscribing}>
                  {tCommon("dictation.button.startNew")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setIsLimitReached(false)}
                >
                  {tCommon("button.close")}
                </Button>
              </div>
            }
          >
            {tCommon("dictation.message.limitReachedDescription")}
          </Modal>
        )}

        {(isTranscribing || isGeneratingNote) && (
          <PageOverlayLoader
            message={
              isTranscribing
                ? tCommon("dictation.message.transcribing")
                : tCommon("dictation.message.generatingNote")
            }
          />
        )}
      </div>
    </div>
  );
}

Dictation.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  transcription: PropTypes.string,
  setProcedureNotes: PropTypes.func,
  hideNoteSaveOption: PropTypes.bool,
};

Dictation.defaultProps = {
  value: "",
  onChange: () => {},
  transcription: "",
  setProcedureNotes: () => {},
  hideNoteSaveOption: false,
};
